import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Activity,
  ChevronDown,
  Flag,
  LayoutDashboard,
  LogOut,
  Search,
  Settings,
  ShieldCheck,
  UserCog,
  Users,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ROUTES } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useAdmin, useAuthStore } from '@/stores/auth-store';

const nav = [
  { to: ROUTES.admin, icon: LayoutDashboard, label: 'Overview' },
  { to: '/admin/verifications', icon: ShieldCheck, label: 'Verifications' },
  { to: ROUTES.adminUsers, icon: Users, label: 'Users' },
  { to: ROUTES.adminManagement, icon: UserCog, label: 'Admins' },
  { to: ROUTES.adminReports, icon: Flag, label: 'Reports' },
  { to: '/admin/audit', icon: Activity, label: 'Audit logs' },
  { to: ROUTES.adminSettings, icon: Settings, label: 'Settings' },
];

export function AdminShell() {
  const admin = useAdmin();
  const signOutAdmin = useAuthStore((s) => s.signOutAdmin);
  const navigate = useNavigate();
  const name = admin?.username ?? admin?.email ?? 'Admin';

  return (
    <div className="grid min-h-dvh grid-cols-[240px_1fr]">
      <aside className="flex flex-col border-e border-(--color-border) bg-(--color-card)">
        <div className="flex h-16 items-center gap-2 border-b border-(--color-border) px-5">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-gradient-to-br from-slate-700 to-slate-900 text-white">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <Link to={ROUTES.admin} className="font-semibold tracking-tight">
            Tally Admin
          </Link>
        </div>
        <nav className="flex-1 px-3 py-3" aria-label="Admin">
          <ul className="space-y-0.5">
            {nav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === ROUTES.admin}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      'text-(--color-muted-foreground) hover:bg-(--color-muted) hover:text-(--color-foreground)',
                      isActive && 'bg-(--color-accent) text-(--color-accent-foreground)',
                    )
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="flex min-h-dvh flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-(--color-border) bg-(--color-background)/90 px-4 backdrop-blur-md sm:px-6 lg:px-8">
          <div className="relative max-w-md flex-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-(--color-muted-foreground)" />
            <input
              type="search"
              placeholder="Search users, cases…"
              className="h-10 w-full rounded-md border border-(--color-input) bg-(--color-background) ps-9 pe-3 text-sm shadow-sm placeholder:text-(--color-muted-foreground) focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:outline-none"
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-(--color-muted) focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:outline-none">
                <Avatar size="sm">
                  <AvatarFallback name={name} />
                </Avatar>
                <ChevronDown className="me-1 h-4 w-4 text-(--color-muted-foreground)" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-(--color-foreground)">{name}</p>
                  <p className="truncate text-xs font-normal tracking-normal text-(--color-muted-foreground) normal-case">
                    {admin?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  signOutAdmin();
                  navigate(ROUTES.adminLogin, { replace: true });
                }}
              >
                <LogOut className="h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main id="main-content" className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="animate-in mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
